import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { toast } from 'react-toastify';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { Store } from '../reducers/index';
import DefaultLayout from '../layouts/DefaultLayout';
import CartItems from './Cart/CartItems';
import Stepper from './Cart/Stepper';
import Paper from '../components/Paper';
import Summary from './Cart/Summary';
import UserAddresses from './Cart/UserAddresses';
import Text from '../components/Text';
import FullPageError from '../components/FullPageError';
import { FaPlus } from 'react-icons/fa';
import { Address, CartItem, User } from '../types';
import Loader from '../components/Loader';
import { RootState } from '../config/store';
import Button from '../components/Button';
import { hideAuthModals, showAddressModal, showContinueRegisterModal, showLoginModal, showPaymentModal, toggleGuestCheckModal } from '../actions/ui';
import DeliveryMethod from './Cart/DeliveryMethod';
import PaymentMethod from './Cart/PaymentMethod';
import PaymentBreakdown from './Cart/PaymentBreakdown';
import { submitOrder } from '../services/ApiService';
import { removeCartBusinessItems } from '../actions/cart';
import { Helmet } from 'react-helmet';
import CheckVoucher from './Cart/CheckVoucher';
import { pageTrack, track } from '../services/mixpanelService';
import PaymentModal from '../components/PaymentModal';
import { FLAVOUR } from '../config/serverInfo';


export interface CartProps {
  history: any
}
const Cart: React.FC<CartProps> = ({ history }) => {
  const isAuthenticated = useSelector<RootState, boolean>(
    (state) => state.auth.isAuthenticated,
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const [deliveryMethod, setDeliveryMethod] = useState('deliver');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentCost, setPaymentCost] = useState(0);

  const [loading, setLoading] = useState(false);
  const [submitingOrder, setSubmitingOrder] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [code, setCode] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const [orderIDs, setOrderIDs] = useState<string[]>([]);
  const cart = useSelector<RootState, CartItem[]>((state) => state.cart);
  // console.log(cart)
  let user = useSelector<RootState, User>((state) => state.auth.user);
  const [currentStep, setCurrentStep] = useState<
    'cart' | 'payment' | 'confirmation'
  >('cart');

  track("view_cart")
  const addresses = useSelector<RootState, Address[]>(
    (state) => state.auth.user ? state.auth.user.addresses : [],
  );
  const addNewAddress = useSelector<RootState, boolean>(
    (state) => state.auth.addNewAddress
  );
  
  const [selectedAddress, setSelectedAddress] = useState<number>(
    addresses[0]?.id ? parseInt(addresses[0]?.id) : 0,
  );
  const [viewCOD, setViewCOD] = useState(
    addresses[0]?.country.is_cash_on_delivery_supported,
  );
  const [tempPaymentData, setTempPaymentData] = useState<{ ids: string[], keys: string[] }>({ ids: [], keys: [] });
  useEffect(() => {
    if(isAuthenticated  &&  user?.is_guest){
      setSelectedAddress(addresses[0]?.id ? parseInt(addresses[0]?.id) : 0);
      setViewCOD(addresses[0]?.country.is_cash_on_delivery_supported);
    }
  }, [isAuthenticated])

  useEffect(() => {
        setSelectedAddress(addresses[0]?.id ? parseInt(addresses[0]?.id) : 0);
        setViewCOD(addresses[0]?.country.is_cash_on_delivery_supported);  
  }, [addNewAddress]);
  
  useEffect(() => {
    if(isAuthenticated){
      if(addresses.length==0){
        dispatch(showAddressModal(false));
      }
    }
    else{
       dispatch(toggleGuestCheckModal(true))
    }
    pageTrack("CART");
  }, [isAuthenticated]);

  const handleChangeAddress = useCallback((address) => {
    setSelectedAddress(address.id);
    setViewCOD(address.country.is_cash_on_delivery_supported)
  }, []);

  const donePayment = useCallback(() => {
    track("purchase", { "currency": "AED" })
    setOrderIDs(tempPaymentData.ids);
    for (let key of tempPaymentData.keys) {
      dispatch(removeCartBusinessItems(key));
    }
    dispatch(hideAuthModals());
  }, [tempPaymentData]);


  const cashOnDelivery = useMemo(() => {
    let selectedAddressObject = addresses.find(address => parseInt(address.id) == selectedAddress)
    let totalPrice = cart.reduce((prev, item) => {
      const itemPrice = item.variant ? item.variant.price : item.product.discountedPrice ? item.product.discountedPrice : item.product.originalPrice;

      return prev + (item.quantity * itemPrice);
    }, 0);
    let value = 0
    if (selectedAddressObject) {
      if(FLAVOUR=="US"){
        if (totalPrice - discount > 160) {
          value = selectedAddressObject.area.cash_on_delivery_high
        } else {
          value = selectedAddressObject.area.cash_on_delivery_low
        }
      }
      else{
        if (totalPrice - discount > 350) {
          value = selectedAddressObject.area.cash_on_delivery_high
        } else {
          value = selectedAddressObject.area.cash_on_delivery_low
        }
      }
      
    }
    if (paymentMethod == "cash_on_delivery") {
      setPaymentCost(value)
    }
    return value
  }, [cart, discount, paymentMethod]);


  const chnagePaymentMethod = useCallback((method) => {
    setPaymentMethod(method);
    if (method == "cash_on_delivery") {
      setPaymentCost(cashOnDelivery)
    } else {
      setPaymentCost(0)
    }
  }, [cashOnDelivery]);


  const handleAddNewAddress = useCallback((address) => {
    if(isAuthenticated){
        dispatch(showAddressModal(false));
    }
    else{
        dispatch(toggleGuestCheckModal(true))
    }
  }, [isAuthenticated]);

  const deliveryCost = useMemo(() => {
    let totalPrice = 0;
    let weight = cart.reduce((prev, item) => {
      const itemPrice = item.variant ? item.variant.price : item.product.discountedPrice ? item.product.discountedPrice : item.product.originalPrice;
      totalPrice += itemPrice * item.quantity
      return prev + (item.quantity * item.product.weight);
    }, 0);
    let selectedAddressObject = addresses.find(address => parseInt(address.id) == selectedAddress)
    let priceRange = selectedAddressObject?.area.prices.find(element => element.max_weight >= weight && element.min_weight <= weight)
    
    if(FLAVOUR=="US"){
      if (totalPrice-discount >= 100) {
        return 0;
      } else {
        if (priceRange) {
          return priceRange.price
        } else if (selectedAddressObject) {
          return selectedAddressObject.area.default_price
        } else { return 0 }
      }
    }
    else{
      if (totalPrice-discount >= 200) {
        return 0;
      } else {
        if (priceRange) {
          return priceRange.price
        } else if (selectedAddressObject) {
          return selectedAddressObject.area.default_price
        } else { return 0 }
      }  
    }
    
  }, [cart,discount]);



  const newVoucher = useCallback((value, code) => {
    setDiscount(value)
    setCode(code)
  }, [dispatch])

  const handleProceed = useCallback(() => {
    
    if (!isAuthenticated) {
      dispatch(showLoginModal());
      return
    }
    
    if (currentStep === 'cart') {
      setPaymentCost(0);
      setCurrentStep('payment');
      track("add_payment_info")
    } else if (currentStep === 'payment') {
      setCurrentStep('confirmation');
    } else {
      (async () => {
        const orders: Record<string, CartItem[]> = {};
        for (let i = 0; i < cart.length; i++) {
          const item = cart[i];
          if (!Array.isArray(orders[item.product.business.id])) {
            orders[item.product.business.id] = [];
          }

          orders[item.product.business.id].push(item);
        }
        try {
          const form = new FormData();
          const keys = [];
          for (let key in orders) {
            console.log("orders[key]")
            console.log(orders[key])
            form.append(
              'orders[]',
              JSON.stringify({
                currency_id: 1,
                business_id: key,
                voucher_code: code,
                delivery_zone_id: orders[key][0].deliveryZone?.id,
                note: orders[key][0].note || '',
                user_delivery_location_id: orders[key][0].address?.id,
                products: orders[key].map((item) => ({
                  id: item.product.id,
                  variant_id: item.variant ? item.variant.id : null,
                  quantity: item.quantity,
                })),
              }),
            );
            form.append(
              "payment_method", paymentMethod
            )
            keys.push(key);
          }
          setSubmitingOrder(true)
          const { ids, client_secret } = await submitOrder(form);
          setSubmitingOrder(false)

          console.log("client_secrettttt")
          console.log(client_secret)
          track("order_cart", { "purchase_type": "cart_checkout" })
          if (paymentMethod == "card") {
            setClientSecret(client_secret)
            setTempPaymentData({ "keys": keys, ids: ids })
            dispatch(showPaymentModal({ "clientSecret": client_secret }));
          } else {
            track("purchase", { "currency": "AED" })
            setOrderIDs(ids);
            for (let key of keys) {
              dispatch(removeCartBusinessItems(key));
            }
          }
        } catch (e) {
          console.error(e);
          // toast.error(e.response.data.error.message[0]);
        }
      })();
    }
  }, [currentStep, cart, user, dispatch]);

  const handleClickStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const subOrders = useMemo(() => {
    const ret: Record<string, CartItem[]> = {};
    cart.forEach((item) => {
      const name = item.product.business.name;
      if (!ret[name]) ret[name] = [];
      ret[name].push(item);
    });

    const prices: any = {};
    Object.keys(ret).forEach((key) => {
      prices[key] = {
        itemsCost: ret[key].reduce((prev, item) => {
          const itemPrice = item.variant
            ? item.variant.price
            : item.product.discountedPrice == item.product.originalPrice || item.product.discountedPrice == 0 ?
              item.product.originalPrice :
              item.product.discountedPrice;
          return itemPrice * item.quantity + prev;
        }, 0),
        deliveryCost: ret[key].reduce((prev, item) => {
          return prev + (item.address?.area?.price || 0);
        }, 0),
        itemsCount: ret[key].length,
      };
    });
    return prices;
  }, [cart]);

  const helmet = (
    <Helmet>
      <title>Cart | twigBIG | Social eCommerce Marketplace in Beauty and Fashion</title>
    </Helmet>

  )
  if (loading) {
    return (
      <>
        {helmet}
        <DefaultLayout history={history}>
          <Loader className={classes.loader} loading={loading} center />
        </DefaultLayout>
      </>
    );
  }

  if (orderIDs.length) {
    return (
      <>
        <DefaultLayout history={history}>
          <div className={classes.thankYouBox}>
            <Text as="h2" align="center">
              You have successfully placed your order
            </Text>
            <Text align="center">
              We will update you on email with the delivery status to track your order
            </Text>
            {orderIDs.map((order) => (
              <Text as="h5" bold align="center" key={order}>
                Order #{order}
              </Text>
            ))}
          </div>
        </DefaultLayout>
      </>
    );
  }

  if (cart.length <= 0) {
    return (
      <>
        {helmet}

        <DefaultLayout history={history}>
          <div className={classes.emptyCartBox}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/img/cart-empty.png`}
              className={classes.emptyImg}
              alt="Empty cart"
            />
            <Text className={classes.emptyText} uppercase>
              Your cart is empty
            </Text>
          </div>
        </DefaultLayout>
      </>
    );
  }



  return (
    <>
      <PaymentModal done={donePayment} />
      {helmet}

      <DefaultLayout history={history}>
        <Container className={clsx(classes.container, 'mt-5')} fluid>
          <Row>
            <Col>
              <Stepper
                steps={['Cart', 'Payment & Delivery', 'Confirmation']}
                currentStep={
                  currentStep === 'cart' ? 0 : currentStep === 'payment' ? 1 : 2
                }
                onClickStep={handleClickStep}
              />
            </Col>
          </Row>
          <Row className={classes.innerContainer}>
            <Col xs={12} lg={8}>

              {currentStep === 'cart' || currentStep == 'confirmation' ? (
                <div>
                  {currentStep !== 'confirmation' ? (
                    <div className="mb-4">
                      <Text className={classes.addressesParagraph} bold>
                        Choose address
                      </Text>
                      <Button
                        className={classes.addAddressBtn}
                        onClick={handleAddNewAddress}
                      >
                        <FaPlus className={classes.addAddressSvg} />
                        <Text className={classes.addressesParagraph} bold>
                          Add New
                        </Text>
                      </Button>
                      <UserAddresses
                        addresses={addresses}
                        handleAddNewAddress={handleAddNewAddress}
                        handleChangeAddress={handleChangeAddress}
                        selectedAddress={selectedAddress}
                      />
                    </div>
                  ) : null}
                  <div>
                    <CartItems
                      cart={cart}
                      editable={currentStep == 'cart'}
                      addresses={addresses}
                      selectedAddress={selectedAddress}
                    />
                  </div>
                  {currentStep === 'confirmation' ? (
                    <PaymentBreakdown discount={discount}
                      orders={subOrders} paymentCost={paymentCost} deliveryCost={deliveryCost} />
                  ) : null}
                </div>
              ) : (
                <div>
                  <div className="mb-3">
                    <DeliveryMethod
                      method={deliveryMethod}
                      onChange={(_, value) => setDeliveryMethod(value)}
                      deliveryCost={deliveryCost}
                    />
                  </div>
                  <div className="mb-3">
                    <PaymentMethod method={paymentMethod}
                      cashOnDelivery={cashOnDelivery}
                      viewCOD={viewCOD}
                      onChange={(_, value) => chnagePaymentMethod(value)}
                    />
                  </div>
                  <div className="mb-3">
                    <CheckVoucher cart={cart} newVoucher={newVoucher} locationId={selectedAddress} deliveryCost={deliveryCost} discountValue={discount} code={code} />
                  </div>
                </div>
              )}
            </Col>
            <Col className={classes.relative} xs={12} lg={4}>
              <div className={classes.absolute}>
                <Summary
                  isSubmitting={selectedAddress == 0 || submitingOrder ? true : false}
                  discount={discount}
                  cart={cart}
                  paymentCost={paymentCost}
                  deliveryCost={deliveryCost}
                  step={currentStep}
                  onProceed={handleProceed}
                />
                
              </div>
            </Col>
          </Row>
        </Container>
      </DefaultLayout>
    </>
  );
};

const useStyles = createUseStyles({
  container: {
    maxWidth: '85vw',
  },
  '@media (max-width: 768px)': {
    container: {
      maxWidth: '850vw',
    }
  },
  loader: {
    marginTop: '40vh',
  },
  innerContainer: {
    "@media (max-width: 1024px)": {
      marginBottom: '70px'
    }
  },
  addressesParagraph: {
    display: 'inline-block',
  },
  addAddressBtn: {
    marginLeft: '10px',
    float: 'right',
    height: '32px',
    borderRadius: '50%',
    padding: 0,
    background: 'none',
    color: '#000',
    '&:hover': {
      color: '#000',
    },
  },
  addAddressSvg: {
    marginBottom: "5px",
    marginRight: "5px"
  },
  summary: {},
  emptyCartBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    minHeight: '70vh',
    color: '#333',
  },
  emptyImg: {
    width: '100%',
    height: '100%',
    maxWidth: '400px',
  },
  emptyText: {
    fontSize: '1rem',
  },
  relative: {
    position: 'relative',
  },
  absolute: {
    position: 'absolute',
    left: '0px',
    right: '0px',
    top: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  thankYouBox: {
    textAlign: 'center',
    marginTop: '4rem',
    '& > h2': {
      color: '#522583 !important',
      fontSize: '3rem',
    },
  },
});

export default Cart;
